import { useEffect, useMemo } from 'react';
import { Vector2Tuple } from 'three';

import { useModelLoader } from '@spazious/dollhouse-engine';
import { useGetFCompositionSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import { useForceUpdate } from '@spazious/react-hooks';

import { FEnsemble } from '../fensembles/index.js';
import { useFurnitureResolver } from '../hooks/index.js';
import { useStore } from '../hooks/useStore.js';
import { Schema } from '../schema/index.js';

export const createHookInstance =
  <TInstance extends FEnsemble>(factory: (id: number, schema: Schema) => TInstance) =>
  (id: number, attrs: Record<string, any>, position: Vector2Tuple, rotation: number): TInstance => {
    const forceUpdate = useForceUpdate();
    const world = useStore(({ world }) => world);

    // Resolve schema for the given type
    const {
      data: {
        fcomposition: { schema },
      },
    } = useGetFCompositionSuspenseQuery({
      variables: { id: id as any },
    });

    // Create an instance of FComposition depending on the type
    // `world` is a stable singleton reference; if the navmesh is ever
    // regenerated live, world.navMeshQuery changes without this memo
    // re-running, so the instance would never pick up the new query. Not an
    // issue today since the navmesh is only baked once from the scene GLTF.
    const instance = useMemo(() => {
      const instance = factory(id, schema);
      instance.setNavMeshQuery(world.navMeshQuery);
      return instance;
    }, [id, schema, world]) as unknown as TInstance;

    // Resolve node types for the given instance
    const nodeTypes = useMemo(
      () => {
        instance.setValues(attrs ?? {});
        instance.position = position;
        instance.rotation = rotation;
        return instance.getNodeTypes();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [attrs, instance, position, rotation]
    );

    // Resolve furniture models for the given node types
    const furnitures = useFurnitureResolver(nodeTypes);
    const furnitureModels = useModelLoader(furnitures.map(({ url }) => url));

    // Set furniture models to the instance
    useEffect(() => {
      instance.setObjects(furnitureModels.map(({ scene }) => scene));
      forceUpdate();
    }, [instance, forceUpdate, furnitureModels]);

    return instance;
  };
